import { useState } from "react";
import { Plus } from "lucide-react";
import {
  Button,
  Card,
  DataList,
  PageHeader,
  SelectField,
  Sheet,
  StatusBadge,
  TextAreaField,
  type DataColumn,
} from "@nahal/ui";
import type { ProvisioningKind, ProvisioningRequest } from "@nahal/api-client";
import { useCancelRequest, useRequests, useSubmitRequest, useOrganizationAccess } from "../../lib/queries";

/**
 * Company and Marketplace are provisioned by Nahal Labs, not self-served -- they
 * are billable resources. This screen is how a customer asks, and how they see
 * where the ask got to.
 */
const kinds: Array<{ value: ProvisioningKind; label: string }> = [
  { value: "COMPANY", label: "A new company" },
  { value: "MARKETPLACE", label: "A new marketplace" },
  { value: "WORKSPACE", label: "A new workspace" },
  { value: "DOMAIN", label: "A custom domain" },
  { value: "QUOTA", label: "More of something (quota)" },
];

export function RequestsScreen() {
  const requests = useRequests();
  const { canManage } = useOrganizationAccess();
  const submit = useSubmitRequest();
  const cancel = useCancelRequest();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<ProvisioningKind>("COMPANY");
  const [note, setNote] = useState("");

  const columns: Array<DataColumn<ProvisioningRequest>> = [
    { key: "kind", header: "Request", primary: true, cell: (row) => row.kind.toLowerCase() },
    { key: "status", header: "Status", cell: (row) => <StatusBadge status={row.status} /> },
    {
      key: "created",
      header: "Asked",
      cell: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
    {
      key: "decision",
      header: "Response",
      desktopOnly: true,
      cell: (row) => row.decisionNote ?? "-",
    },
  ];

  return (
    <>
      <PageHeader
        title="Requests"
        description="Ask us for a company, a marketplace or more capacity."
        actions={canManage ? (
          <Button variant="solid" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>
            New request
          </Button>
        ) : undefined}
      />

      <Card>
        <DataList
          columns={columns}
          rows={requests.data ?? []}
          rowKey={(row) => row.id}
          loading={requests.isLoading}
          error={requests.isError ? "Could not load requests." : null}
          empty={{
            title: "No requests yet",
            description: "Need another company or marketplace? Ask here and we will set it up.",
          }}
          rowActions={(row) =>
            canManage && row.status === "PENDING" ? (
              <Button size="sm" variant="ghost" onClick={() => cancel.mutate(row.id)}>
                Cancel
              </Button>
            ) : null
          }
        />
      </Card>

      <Sheet
        open={open && canManage}
        title="New request"
        description="Tell us what you need and we will set it up."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              size="lg"
              disabled={submit.isPending}
              onClick={() =>
                submit.mutate(
                  { kind, note },
                  {
                    onSuccess: () => {
                      setOpen(false);
                      setNote("");
                    },
                  },
                )
              }
            >
              {submit.isPending ? "Sending..." : "Send request"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <SelectField
            label="What do you need?"
            options={kinds}
            value={kind}
            onChange={(event) => setKind(event.target.value as ProvisioningKind)}
          />
          <TextAreaField
            label="Details"
            hint="Names, addresses, anything we will need."
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      </Sheet>
    </>
  );
}
